import { Copy } from '@ev-tech/ui-core-components/Copy';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';
import { Overline } from '@ev-tech/ui-core-components/Overline';
import { InfoIconButton } from '@ev-tech/ui-core-components/Tooltip';
import CrossNaked from '@pkgs/components/assets/icons/xsmall/Cross-Naked.svg';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { getPreferenceTranslation } from '@pkgs/components/utils/aiSearchPreferenceUtils';
import { type ReactElement, useRef } from 'react';

import { Headline } from '../Headline/Headline';
import { Popover,PopoverClose, PopoverRoot, PopoverTrigger } from '../Popover/Popover';
import { type PreferencesObject } from '../SearchResultCard';
import { LinearGradientProgressBar } from './LinearGradientProgressBar';
import { PreferencesDetailsModal } from './PreferencesDetailsModal';

import styles from './PreferenceScores.module.css';
import { useModalState } from '@ev-tech/ui-core-components';

type PreferencesScoresDropdownProps = {
  isMapView?: boolean;
  preferences: PreferencesObject;
  trigger: ReactElement;
};

export const PreferenceScores = ({ isMapView, preferences, trigger }: PreferencesScoresDropdownProps) => {
  const { t } = useAppContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const preferencesInfoModalState = useModalState();

  return (
    <>
      <PopoverRoot modal="trap-focus">
        <PopoverTrigger render={trigger} />
        <Popover
          aria-label={t('search.aiSearch.scoresDropdown.title')}
          backdropClassName={styles.backdrop}
          className={isMapView ? styles.mapPopover : styles.popover}
          drawer
          onBackdropClick={(event) => event.stopPropagation()}
        >
          <div className={styles.preferenceScoresContainer} onClick={(event) => event.stopPropagation()}>
            <div className={styles.headingSection}>
              <Headline size="h6" label={t('search.aiSearch.scoresDropdown.title')} />
              <InfoIconButton
                onClick={(e) => {
                  e.stopPropagation();
                  preferencesInfoModalState.open();
                }}
                label={t('search.aiSearch.scoresDropdown.infobutton.title')}
                ref={buttonRef}
              />
            </div>

            <PopoverClose
              render={<IconButton as="button" className={styles.closeButton} icon={<CrossNaked />} variant="tertiary" label="Close" />}
            />
            <div className={styles.overallMatchContainer}>
              <Copy className={styles.gradientText} text={t('search.aiSearch.scoresDropdown.overallMatch')} size="m" bold />
              <div className={styles.progressBarWrapper}>
                <Copy
                  className={styles.gradientText}
                  text={`${preferences.overallLabel} (${preferences.overallPercentage})`}
                  size="m"
                  bold
                />
                <LinearGradientProgressBar value={Math.floor(preferences.overallScore)} />
              </div>
            </div>
            {preferences.matchingPositivePreferenceMatchs.length > 0 && (
              <div className={styles.preferenceScoresList}>
                <Overline text={t('search.aiSearch.scoresDropdown.matchingPreferences')} />
                {preferences.matchingPositivePreferenceMatchs.map(([key, value, percentage]) => (
                  <div key={key} className={styles.scoreItem}>
                    <Copy text={getPreferenceTranslation(key, t)} size="m" />
                    <div className={styles.progressBarWrapper}>
                      <Copy text={value} size="m" />
                      <LinearGradientProgressBar value={Math.floor(percentage * 100)} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {preferences.matchingPoorPreferences.length > 0 && (
              <div className={styles.preferenceScoresList}>
                <Overline text={t('search.aiSearch.scoresDropdown.otherPreferences')} />
                <Copy
                  text={preferences.matchingPoorPreferences.map((preference) => getPreferenceTranslation(preference, t)).join(', ')}
                  size="m"
                />
              </div>
            )}
          </div>
        </Popover>
      </PopoverRoot>
      <PreferencesDetailsModal modalState={preferencesInfoModalState} />
    </>
  );
};
